/**
 * Corpo bruto de GET CommunicationSubscribers/myCommunications/:id
 * e lista paginada, alinhado ao que `formatCommunication` espera em CommunicationService.
 *
 * Tipos (CommunicationList#getTypeLabel): 1 Notícia, 2 Evento, 3 Informe, 4 Teste, 5 Feed de Rotina.
 * `interaction.hasAck === false` sinaliza ciência pendente (como no app de responsáveis).
 */

function isoHoursAgo(h) {
  const d = new Date();
  d.setHours(d.getHours() - h);
  return d.toISOString();
}

function isoDaysFromNow(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  d.setHours(9, 0, 0, 0);
  return d.toISOString();
}

/**
 * Capas fixas (Unsplash) por id — tema escola/reunião, evitando fotos aleatórias do Picsum.
 */
function coverPhoto(communicationId) {
  const id = Number(communicationId);
  const urls = {
    1: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=800&q=80",
    2: "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?auto=format&fit=crop&w=800&q=80",
    3: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&w=800&q=80",
    4: "https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?auto=format&fit=crop&w=800&q=80",
    5: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=800&q=80",
    6: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=800&q=80"
  };
  return urls[id] || urls[1];
}

/**
 * Registros por communication.id.
 * subscriber.id alinhado a buildDemoNotifications (501 e 503 para comunicados 1 e 2).
 */
const BY_COMMUNICATION_ID = {
  1: {
    id: 501,
    userId: 1,
    communication: {
      id: 1,
      title: "Reunião de pais — turmas do ensino fundamental",
      updatedAt: isoHoursAgo(3),
      type: 1,
      photo: coverPhoto(1),
      bodyMessage:
        "<p>Prezado(a) professor(a),</p><p>Encaminhamos o comunicado enviado às famílias sobre a <strong>reunião de pais</strong> do dia <strong>25/04</strong>, no auditório.</p><p>No modo demonstração Waybee, esta mensagem simula o que a escola divulga; o conteúdo pedagógico das suas turmas continua no diário de classe.</p>",
      eventStart: null,
      buttonText: "",
      linkAddress: "",
      creator: { photoProfile: null },
      attachments: []
    },
    interaction: {
      openedAt: null,
      clickedAt: null,
      hadInterestAt: null,
      confirmedPresenceAt: null
    }
  },
  2: {
    id: 503,
    userId: 1,
    communication: {
      id: 2,
      title: "Circular: calendário de provas bimestrais publicado",
      updatedAt: isoHoursAgo(30),
      type: 3,
      photo: coverPhoto(2),
      bodyMessage:
        "<p>O coordenação pedagógica publicou o <strong>calendário de avaliações bimestrais</strong>. Consulte os prazos por disciplina e alinhe seus planos de aula no Class Diary.</p><p>Em caso de dúvida sobre datas, use o canal de suporte Waybee.</p>",
      eventStart: null,
      buttonText: "",
      linkAddress: "",
      creator: { photoProfile: null },
      attachments: [
        {
          id: 1,
          customName: "Calendario-provas-bimestre-demo.pdf",
          url: "https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf"
        }
      ]
    },
    interaction: {
      openedAt: null,
      clickedAt: null,
      hadInterestAt: null,
      confirmedPresenceAt: null
    }
  },
  3: {
    id: 506,
    userId: 1,
    communication: {
      id: 3,
      title: "Evento: capacitação Waybee — confirme sua presença",
      updatedAt: isoHoursAgo(52),
      type: 2,
      photo: coverPhoto(3),
      bodyMessage:
        "<p>Encontro presencial para professores sobre o <strong>Class Diary</strong>.</p><p><strong>Confirme presença</strong> nos botões do comunicado (mesmo fluxo do app para famílias): interesse e presença no evento.</p>",
      eventStart: isoDaysFromNow(14),
      buttonText: "Abrir inscrição",
      linkAddress: "https://waybee.com.br",
      creator: { photoProfile: null },
      attachments: []
    },
    interaction: {
      openedAt: null,
      clickedAt: null,
      hadInterestAt: null,
      confirmedPresenceAt: null
    }
  },
  4: {
    id: 507,
    userId: 1,
    communication: {
      id: 4,
      title: "Pesquisa rápida: experiência com o diário digital",
      updatedAt: isoHoursAgo(120),
      type: 4,
      photo: coverPhoto(4),
      bodyMessage:
        "<p>Convidamos você a responder uma pesquisa curta (modo demo) sobre a experiência com o diário. Os resultados ajudam a priorizar melhorias no produto Waybee.</p>",
      eventStart: null,
      buttonText: "",
      linkAddress: "",
      creator: { photoProfile: null },
      attachments: []
    },
    interaction: {
      openedAt: null,
      clickedAt: null,
      hadInterestAt: null,
      confirmedPresenceAt: null
    }
  },
  5: {
    id: 508,
    userId: 1,
    communication: {
      id: 5,
      title: "Rotina: registros de aula e frequência da semana",
      updatedAt: isoHoursAgo(168),
      type: 5,
      photo: coverPhoto(5),
      bodyMessage:
        "<p>Lembrete para manter <strong>frequência</strong> e <strong>conteúdos</strong> atualizados nas turmas em que leciona. O padrão segue o mesmo fluxo do Class Diary em produção.</p>",
      eventStart: null,
      buttonText: "",
      linkAddress: "",
      creator: { photoProfile: null },
      attachments: []
    },
    interaction: {
      openedAt: null,
      clickedAt: null,
      hadInterestAt: null,
      confirmedPresenceAt: null
    }
  },
  6: {
    id: 509,
    userId: 1,
    communication: {
      id: 6,
      title: "Normas de avaliação 2026 — ciência obrigatória (professores)",
      updatedAt: isoHoursAgo(6),
      type: 3,
      photo: coverPhoto(6),
      bodyMessage:
        "<p>Documento institucional com critérios de avaliação e prazos. É <strong>obrigatório dar ciência</strong> após a leitura (o Class Diary exibirá o aviso até você confirmar).</p><p>Este fluxo replica o comportamento do app de alunos e responsáveis.</p>",
      eventStart: null,
      buttonText: "",
      linkAddress: "",
      creator: { photoProfile: null },
      attachments: []
    },
    interaction: {
      openedAt: null,
      hasAck: false,
      clickedAt: null,
      hadInterestAt: null,
      confirmedPresenceAt: null
    }
  }
};

const SORTED_COMMUNICATION_IDS = Object.keys(BY_COMMUNICATION_ID).sort(
  (a, b) =>
    new Date(BY_COMMUNICATION_ID[b].communication.updatedAt).getTime() -
    new Date(BY_COMMUNICATION_ID[a].communication.updatedAt).getTime()
);

function cloneSubscriber(key) {
  return JSON.parse(JSON.stringify(BY_COMMUNICATION_ID[key]));
}

/**
 * Payload para GET CommunicationInteractions/getPendingAckCommunications → AckDialog.
 */
export function buildDemoPendingAckCommunication() {
  const row = BY_COMMUNICATION_ID["6"];
  const c = row.communication;
  return {
    id: c.id,
    userId: row.userId,
    title: c.title,
    bodyMessage: c.bodyMessage,
    type: c.type,
    eventStart: c.eventStart,
    createdAt: c.updatedAt
  };
}

/** Chamado após POST updateAck na demo — lista GET passa a refletir ciência confirmada. */
export function demoMarkAcknowledgeDone() {
  const row = BY_COMMUNICATION_ID["6"];
  if (row && row.interaction) {
    row.interaction.hasAck = true;
  }
}

/**
 * Não lidos para WaybeeUsers/mySessionInfo (badge do menu).
 */
export function getDemoCommunicationsUnreadCount() {
  return Object.keys(BY_COMMUNICATION_ID).filter(
    k => !BY_COMMUNICATION_ID[k].interaction.openedAt
  ).length;
}

/**
 * Lista para GET `CommunicationSubscribers/myCommunications` (página 0 na demo).
 */
export function getDemoCommunicationsSubscribersList() {
  return SORTED_COMMUNICATION_IDS.map(k => cloneSubscriber(k));
}

export function getDemoCommunicationsCount() {
  return Object.keys(BY_COMMUNICATION_ID).length;
}

/**
 * Resposta de PUT `Communications/:id/read` (ver communication-interactions.js).
 */
export function buildDemoCommunicationReadResponse(communicationId) {
  const key = String(communicationId);
  const row = BY_COMMUNICATION_ID[key];
  if (!row) {
    return { communications: [], notifications: [] };
  }
  return {
    communications: [
      {
        communicationSubscriberId: row.id,
        userId: row.userId
      }
    ],
    notifications: []
  };
}

/**
 * @param {string} segment — id do comunicado (communication.id) na URL
 */
export function getDemoCommunicationSubscriber(segment) {
  const key = String(segment);
  if (BY_COMMUNICATION_ID[key]) {
    return cloneSubscriber(key);
  }
  return cloneSubscriber("1");
}
